import { isAxiosError } from 'axios'

import { ApiError } from 'api/errors'
import axiosInstance from './axios'
import {
  clearTokens,
  expiresInSeconds,
  getTokens,
  setTokens,
  StoredTokens
} from './jwtStorage'

const REFRESH_BEFORE_EXPIRY_SECONDS = 300

// A refresh that got no answer about the session is tried again 5s later, then
// 10s, 20s and so on, never more than a minute apart, until one gets an answer.
const RETRY_FIRST_DELAY_MS = 5_000
const RETRY_MAX_DELAY_MS = 60_000

// The API's word that the refresh token is finished. Cognito's REFRESH_TOKEN_AUTH
// answers NotAuthorizedException for an expired, revoked or invalid refresh token
// (and for a disabled customer), and the API hands the code back in an HTTP 200
// { status: false, data: { code } } envelope (goldie-cloud
// api_router_cognito.refresh_token), which utils/axios rejects as an ApiError.
const SESSION_ENDED_CODES = new Set([
  'NotAuthorizedException',
  'UserNotFoundException',
  'UserNotConfirmedException',
  'PasswordResetRequiredException',
  'InvalidParameterException'
])
const SESSION_ENDED_STATUSES = new Set([400, 401, 403])

let refreshHandle: ReturnType<typeof setTimeout> | null = null
let inflightRefresh: Promise<void> | null = null
let failedAttempts = 0

const clearTimer = (): void => {
  if (refreshHandle !== null) {
    clearTimeout(refreshHandle)
    refreshHandle = null
  }
}

export const cancelRefresh = (): void => {
  clearTimer()
  failedAttempts = 0
}

const expireAuth = (): void => {
  cancelRefresh()
  clearTokens()
  window.location.replace('/login')
}

/**
 * Did the refresh endpoint say the refresh token is no good? Only then is the
 * customer signed out.
 *
 * Nothing else says anything about the session: no response at all (offline, a
 * timeout, a request the browser cancelled or aborted, a CORS "access control
 * checks" failure), a 5xx, a throttle, or an answer without tokens. WebKit reports
 * a refresh that a page load cut off as exactly such a failure and runs the page's
 * error handler for it (Chromium runs none), so treating every failure as an
 * expired session signed iPhone customers out whenever they navigated while a
 * refresh was in flight.
 */
const refreshTokenRejected = (error: unknown): boolean => {
  if (error instanceof ApiError) {
    return error.status < 500 && SESSION_ENDED_CODES.has(error.code)
  }
  if (!isAxiosError(error) || !error.response) return false

  const { status } = error.response
  if (SESSION_ENDED_STATUSES.has(status)) return true
  if (status >= 500) return false

  const body = error.response.data as
    | { code?: string; data?: { code?: string } | null }
    | null
    | undefined
  const code = body?.code ?? body?.data?.code
  return code !== undefined && SESSION_ENDED_CODES.has(code)
}

// Try again later after a refresh that got no answer, backing off. Not once the
// customer has signed out: nothing is left to refresh.
const retryLater = (): void => {
  clearTimer()
  if (getTokens() === null) return

  failedAttempts += 1
  const delayMs = Math.min(
    RETRY_FIRST_DELAY_MS * 2 ** (failedAttempts - 1),
    RETRY_MAX_DELAY_MS
  )
  refreshHandle = setTimeout(() => {
    refreshHandle = null
    void refreshAccessToken().catch(() => undefined)
  }, delayMs)
}

const requestNewTokens = async (
  tokens: StoredTokens
): Promise<StoredTokens> => {
  const response = await axiosInstance.post('/auth/refresh-token', {
    refreshToken: tokens.refreshToken
  })

  const data = response.data as Partial<StoredTokens> | null | undefined
  if (!data?.accessToken || !data?.expiresAt) {
    throw new Error('The token refresh answered without tokens')
  }
  return {
    accessToken: data.accessToken,
    refreshToken: data.refreshToken || tokens.refreshToken,
    expiresAt: data.expiresAt
  }
}

const _doRefresh = async (): Promise<void> => {
  const tokens = getTokens()
  if (tokens === null) {
    expireAuth()
    throw new Error('No session to refresh')
  }

  const refreshed = await requestNewTokens(tokens).catch((error: unknown) => {
    // Sign out only on the API's say-so. Otherwise the tokens stay as they are:
    // the next request that needs a refresh asks again, and so does the retry.
    if (refreshTokenRejected(error)) {
      expireAuth()
    } else {
      retryLater()
    }
    throw error
  })

  setTokens(refreshed)
  scheduleRefresh()
}

/**
 * Refresh the access token. Resolves once new tokens are stored; rejects when the
 * refresh failed, after either signing the customer out (the API refused the
 * refresh token) or keeping the tokens and scheduling another try (any other
 * failure). Concurrent calls share one request.
 */
export const refreshAccessToken = (): Promise<void> => {
  if (inflightRefresh !== null) return inflightRefresh
  inflightRefresh = _doRefresh().finally(() => {
    inflightRefresh = null
  })
  return inflightRefresh
}

export const scheduleRefresh = (): void => {
  cancelRefresh()

  const secondsRemaining = expiresInSeconds()
  const delayMs = (secondsRemaining - REFRESH_BEFORE_EXPIRY_SECONDS) * 1000

  if (delayMs <= 0) {
    void refreshAccessToken().catch(() => undefined)
  } else {
    refreshHandle = setTimeout(() => {
      void refreshAccessToken().catch(() => undefined)
    }, delayMs)
  }
}

export const startMultiTabSync = (): void => {
  window.addEventListener('storage', (event: StorageEvent) => {
    if (event.key !== 'goldie:auth:accessToken') return

    if (event.newValue !== null) {
      // Another tab updated the token — reschedule refresh with the new expiry
      cancelRefresh()
      scheduleRefresh()
    } else {
      // Another tab cleared the tokens — mirror the logout
      clearTokens()
      window.location.replace('/login')
    }
  })
}
